import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import Search from 'apollo-react/components/Search';
import ChevronDown from 'apollo-react-icons/ChevronDown';
import ChevronUp from 'apollo-react-icons/ChevronUp';
import classNames from 'classnames';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { handleEditorSearch, switchEditorSearchResult } from '../../actions/editor.actions';
import { FORM_KEYS } from '../../constants/form-manager.constants';
import { AUTOCOMPLETE_SEARCH_DELAY } from '../../constants/runtime.constants';
import { getSearchResult } from '../../selectors/common.selectors';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    margin: 0,
    width: 330,
    '& label': {
      display: 'none !important',
    },
    '& p': {
      display: 'none !important',
    },
  },
  resultCount: {
    minWidth: 75,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    top: 1,
    padding: '0 12px',
    fontSize: 18,
  },
  divider: {
    marginRight: 6,
    height: 40,
    width: 1,
    backgroundColor: '#fff',
  },
  active: {
    color: '#fff',
  },
  inactive: {
    color: '#5a5a5a',
  },
});

const SearchBar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const timeoutRef = useRef(null);
  const isKeyDownReadyRef = useRef(true);

  const { searchIdIndex = 0, searchIdsCount = 0 } = useFormSelector(getSearchResult, {
    formKey: FORM_KEYS.EDITOR,
  });

  const handleChange = ({ target }) => {
    clearTimeout(timeoutRef.current);
    isKeyDownReadyRef.current = false;

    timeoutRef.current = setTimeout(async () => {
      dispatch(handleEditorSearch(target.value));
      isKeyDownReadyRef.current = true;
    }, AUTOCOMPLETE_SEARCH_DELAY);
  };

  const handleKeyDown = ({ key, shiftKey }) => {
    if (!isKeyDownReadyRef.current || !searchIdsCount || key !== 'Enter') {
      return;
    }

    dispatch(switchEditorSearchResult(!shiftKey));
  };

  return (
    <div className={classes.root}>
      <Search
        className={classes.search}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={translateString('Search in a form')}
      />
      <div
        className={classNames({
          [classes.resultCount]: true,
          [classes.inactive]: !searchIdsCount,
        })}
      >
        {`${!searchIdsCount ? 0 : searchIdIndex + 1}/${searchIdsCount}`}
      </div>
      <div className={classes.divider} />
      <IconButton
        size="small"
        disabled={!searchIdsCount}
        onClick={() => dispatch(switchEditorSearchResult(false))}
      >
        <ChevronUp className={searchIdsCount ? classes.active : classes.inactive} />
      </IconButton>
      <IconButton
        size="small"
        disabled={!searchIdsCount}
        onClick={() => dispatch(switchEditorSearchResult(true))}
      >
        <ChevronDown className={searchIdsCount ? classes.active : classes.inactive} />
      </IconButton>
    </div>
  );
};

export default SearchBar;
